<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { performanceMark } from '@libs/utils';

import { useBrowserStore } from '../../core/stores';

import HotelSelectionContentDesktop from './components/HotelSelectionContentDesktop.vue';
import HotelSelectionContentMobile from './components/HotelSelectionContentMobile.vue';
import HotelSelectionDesktopLayout from './components/HotelSelectionDesktopLayout.vue';
import HotelSelectionHeaderDesktop from './components/HotelSelectionHeaderDesktop.vue';
import HotelSelectionHeaderMobile from './components/HotelSelectionHeaderMobile.vue';
import HotelSelectionMobileLayout from './components/HotelSelectionMobileLayout.vue';
import HotelSelectionModals from './components/HotelSelectionModals.vue';
import ListMapSwitch from './components/ListMapSwitch.vue';
import { useHotelSelectionStore, useHotelSelectionMapStore } from './stores';
import { HotelSelectionViewType } from './models';

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const route = useRoute();

onMounted(() => {
  performanceMark('HotelSelectionViewInitiated');
  // HACK: scroll top to avoid iOS Safari address bar issue
  // When users have scroll bar on top in iOS Safari, safari wrongly renders hotel selection page when navigating from other pages.
  // Forcing app to sroll top 1px will fix the issue.
  if (browserStore.isSmallerThan600px) {
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 0);
  }

  const index = route.params.recipeIndex;
  if (typeof index === 'string') {
    const indexAsNumber = parseInt(index);
    hotelSelectionStore.initializeHotelSelectionView(indexAsNumber);
  }

  hotelSelectionMapStore.destroyMap(); // HACK: This is a way to fix zooming out map in hotel selection page https://trello.com/c/DvEarbjd
});

onUnmounted(() => {
  hotelSelectionStore.resetHotelSelectionView();
});

const hasInitializeHotelSelectionList = ref<boolean>(false);
const isOpeningPriceCalendar = ref<boolean>(false);

function onInitializeHotelSelectionList(): void {
  if (hasInitializeHotelSelectionList.value) {
    return;
  }
  hotelSelectionStore.handleInitializedEvent();
  hasInitializeHotelSelectionList.value = true;
}

function onShowPriceRibbon(): void {
  isOpeningPriceCalendar.value = true;
  hotelSelectionStore.showPriceRibbon();

  // HACK: block long scroll event until price calendar is fully opened
  const PRICE_CALENDAR_TRANSITION_TIME_IN_MS = 500; // price calendar transition duration
  setTimeout(() => {
    isOpeningPriceCalendar.value = false;
  }, PRICE_CALENDAR_TRANSITION_TIME_IN_MS);
}

function onLongScroll(): void {
  if (isOpeningPriceCalendar.value) {
    return;
  }
  hotelSelectionStore.hidePriceRibbon();
}
</script>

<template>
  <!-- NOTE: Hotel details panel is mounted via child route -->
  <router-view />

  <HotelSelectionModals />

  <HotelSelectionMobileLayout
    v-if="browserStore.isSmallerThan1200px"
    :has-fixed-height="
      hotelSelectionStore.viewType === HotelSelectionViewType.Map
    "
    @long-scroll="onLongScroll"
  >
    <template #header>
      <HotelSelectionHeaderMobile @show-price-ribbon="onShowPriceRibbon" />
    </template>
    <template #content>
      <HotelSelectionContentMobile
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
    <template #list-map-switch>
      <ListMapSwitch />
    </template>
  </HotelSelectionMobileLayout>

  <HotelSelectionDesktopLayout v-else>
    <template #upper-panel>
      <HotelSelectionHeaderDesktop />
    </template>
    <template #lower-panel>
      <HotelSelectionContentDesktop
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
  </HotelSelectionDesktopLayout>
</template>
